import { Alert, Icon, Text, View } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { StyleSheet } from 'react-native';
import { ToastOptions } from 'react-native-toast-notifications';

import { ISanityImage } from '@rbi-ctg/menu';
import Picture from 'components/picture';
import { ISanityImage as GWISanityImage } from 'generated/graphql-gateway';
import { IImageFragment } from 'generated/sanity-graphql';
import { primitive } from 'styles/constants/primitives';

import { modalToaster, rootToaster } from './refs';

const getToaster = () => modalToaster.current || rootToaster.current;

// applying defaults here because for some reason having multiple providers (as we need for modals) neither provider works for customization
// NOTE! there is also a patch-package file for this library for some other issues
const defaults: ToastOptions = {
  style: { backgroundColor: 'transparent', marginBottom: spacing.$2, padding: 0 },
  placement: 'top',
  duration: 3000, // per design
};

export const StandardToast: React.FC<{
  text: string;
  variant: 'info' | 'success' | 'error' | 'neutral';
}> = ({ text, variant }) => (
  <Alert
    maxWidth="100%"
    alignSelf="center"
    flexDirection="row"
    status={variant || 'neutral'}
    variant="black"
    accessibilityLiveRegion="polite"
    accessibilityHint=""
    marginX={0}
    marginBottom={0}
    alignItems="center"
  >
    {variant !== 'neutral' && <Alert.Icon />}
    <Text fontSize="md" fontWeight="medium" color="white" flexShrink={1} marginLeft={3}>
      {text}
    </Text>
  </Alert>
);

// this is because a modal could be dismissing and we need the toast on the root view not on the now gone modal
const TOAST_DELAY = 200;

const addToCartToast = (
  message: string,
  image?: ISanityImage | IImageFragment | GWISanityImage | null,
  onRootToaster = true
) => {
  setTimeout(() => {
    (onRootToaster ? rootToaster.current : getToaster())?.show(
      <View style={styles.addToCartWrap}>
        {image && (
          <View style={styles.pictureContainer}>
            <Picture alt={message.toString()} height="$12" width="$12" image={image} />
            <View style={styles.iconContainer}>
              <Icon variant="checkMarkGreen" size="$6" />
            </View>
          </View>
        )}
        <TextMessage>{message}</TextMessage>
      </View>,
      defaults
    );
  }, TOAST_DELAY);
};

const showToast = (message: string, variant: 'info' | 'success' | 'error' | 'neutral') => {
  setTimeout(() => {
    getToaster()?.show(<StandardToast text={message} variant={variant} />, defaults);
  }, TOAST_DELAY);
};

export const toaster = {
  addToCartToast,
  informative: (message: string) => showToast(message, 'info'),
  positive: (message: string) => showToast(message, 'success'),
  negative: (message: string) => showToast(message, 'error'),
  neutral: (message: string) => showToast(message, 'neutral'),
};

const styles = StyleSheet.create({
  addToCartWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.$3,
    marginVertical: spacing.$1,
    backgroundColor: primitive.$white,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: primitive.$blackOpacity4,
    elevation: 2,
  },
  pictureContainer: {
    height: 60,
    width: 60,
    position: 'relative',
    borderWidth: 1,
    borderColor: primitive.$blackOpacity30,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainer: {
    position: 'absolute',
    right: -4,
    top: -4,
    height: 15,
    width: 15,
  },
});

export const TextMessage = Text.withConfig({
  variant: 'copyOne',
  fontWeight: 'bold',
  flex: 1,
  marginLeft: spacing.$1,
});
